<script lang="ts" setup>
import { syncedRanges } from '@thecompaniesapi/shared/client'
import type { UInputMenu } from '#components'

const props = defineProps({
  attribute: {
    required: true,
    type: String as PropType<SegmentationAttribute>,
  },
  companies: {
    default: () => [],
    type: Array as PropType<CompanyV2[]>,
  },
  currentValues: {
    default: () => [],
    type: Array as PropType<(string | undefined)[]>,
  },
  showBrowse: {
    default: true,
    type: Boolean,
  },
})

const emit = defineEmits<{
  inputClick: [event: Event]
  inputFocused: [event: FocusEvent]
  submitSuggestionQuery: [event: KeyboardEvent]
  suggestionSelected: [option: UInputMenuOption]
}>()

const { t } = useI18n()
const { getIndexForAttribute } = useSegmentation()
const { toggleSlideover } = useOverlays()
const suggestionsStore = useSuggestionsStore()
const listsStore = useListsStore()

const suggestionQuery = defineModel<string>('query', {
  default: '',
  required: false,
})

const suggestions = defineModel<UInputMenuOption[]>('suggestions', {
  default: () => [],
  required: false,
})

const loadingSuggestions = defineModel<boolean>('loadingSuggestions', {
  default: false,
  required: false,
})

const hasBrowsePanel = computed(() => {
  return !['meta.listIds'].includes(props.attribute)
})

const inputMenuRef = ref<InstanceType<typeof UInputMenu> | null>(null)

function onInputClick(event: Event) {
  // Open input menu suggestions
  ;(inputMenuRef.value as any)?.trigger?.$el.click()

  emit('inputClick', event)
}

function onInputFocused(event: FocusEvent) {
  if (!suggestionQuery.value) {
    generateSuggestions()
  }

  emit('inputFocused', event)
}

async function fetchSuggestions() {
  loadingSuggestions.value = true

  const index = getIndexForAttribute(props.attribute)

  if (index) {
    suggestions.value = await suggestionsStore.fetchSuggestions({
      index,
      minPopulation: index !== 'countries' ? undefined : 10 * 1000,
      page: 1,
      search: suggestionQuery.value,
    })
  }

  loadingSuggestions.value = false
}

function generateSuggestions() {
  const generatedSuggestions = []

  // Or display the suggestions fetched from the server
  switch (props.attribute) {
    case 'about.businessType':
      suggestions.value = suggestionsStore.businessTypes.map(item => ({
        icon: getIcon('businessType'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'apps':
      suggestions.value = suggestionsStore.apps.map(item => ({
        icon: getIcon('apps'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'about.industries':
    case 'about.industry':
      if (!suggestionQuery.value && !props.companies.length) {
        for (const company of props.companies) {
          const mergedIndustries = Array.from(new Set([...(company.about?.industries || []), company.about?.industry])).filter(Boolean)

          if (mergedIndustries.length) {
            for (const companyIndustry of mergedIndustries) {
              const existingSuggestion = (suggestions?.value || []).find(suggestion => suggestion.value === companyIndustry)

              if (!existingSuggestion) {
                generatedSuggestions.push({
                  count: 1,
                  icon: getIcon('industries'),
                  label: changeCase.noCase(companyIndustry || ''),
                  value: companyIndustry,
                })
              }
              else {
                existingSuggestion.count! += 1
              }
            }
          }
        }
      }

      if (generatedSuggestions.length) {
        suggestions.value = generatedSuggestions
      }
      else {
        suggestions.value = suggestionsStore.industries.map(item => ({
          icon: getIcon('industries'),
          label: item.label,
          value: item.value,
        }))
      }

      break

    case 'about.totalEmployees':
      suggestions.value = suggestionsStore.totalEmployees.map(item => ({
        icon: getIcon('totalEmployees'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'analytics.monthlyVisitors':
      suggestions.value = suggestionsStore.monthlyVisitors.map(item => ({
        circle: getColorForAttribute('monthlyVisitors', item.value),
        label: item.label,
        value: item.value,
      }))
      break

    case 'codes.naics':
      suggestions.value = suggestionsStore.industriesNaics.map(item => ({
        icon: getIcon('industryNaics'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'codes.sic':
      suggestions.value = suggestionsStore.industriesSic.map(item => ({
        icon: getIcon('industrySic'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'domain.tld':
      suggestions.value = suggestionsStore.tlds.map(item => ({
        icon: getIcon('domainTld'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'finances.revenue':
      suggestions.value = suggestionsStore.revenues.map(item => ({
        icon: getIcon('revenue'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'finances.stockExchange':
      suggestions.value = suggestionsStore.stockExchanges.map(item => ({
        icon: getIcon('stockExchanges'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'locations.headquarters.city.code':
      suggestions.value = suggestionsStore.cities.map(item => ({
        icon: `flagpack:${item.flag}`,
        label: item.label,
        value: item.value,
      }))
      break

    case 'locations.headquarters.continent.code':
      suggestions.value = suggestionsStore.continents.map(item => ({
        icon: getIcon('continent'),
        label: item.label,
        value: item.value,
      }))
      break

    case 'locations.headquarters.country.code':
      suggestions.value = suggestionsStore.countries.map(item => ({
        icon: `flagpack:${item.flag}`,
        label: item.label,
        value: item.value,
      }))
      break

    case 'locations.headquarters.county.code':
      suggestions.value = suggestionsStore.counties.map(item => ({
        icon: `flagpack:${item.flag}`,
        label: item.label,
        value: item.value,
      }))
      break

    case 'locations.headquarters.state.code':
      suggestions.value = suggestionsStore.states.map(item => ({
        icon: `flagpack:${item.flag}`,
        label: item.label,
        value: item.value,
      }))
      break

    case 'meta.listIds':
      suggestions.value = listsStore.lists.map(item => ({
        icon: getIcon('list'),
        label: truncate(item.name, 25),
        value: item.id,
      }))

      return suggestions.value

    case 'socials':
      suggestions.value = suggestionsStore.socialNetworks.map(item => ({
        icon: getIcon(item.value as UIcon),
        label: item.label,
        value: item.value,
      }))
      break

    case 'meta.syncedAt':
      suggestions.value = syncedRanges.lastRanges.map(
        range => ({
          icon: getIcon('history'),
          label: t(`ranges.dates.${range.value}`),
          value: range.value,
        }),
      )

      return suggestions.value

    case 'technologies.active':
      suggestions.value = suggestionsStore.technologies.map((item) => {
        const suggestion = {
          avatar: undefined as string | undefined,
          icon: undefined as string | undefined,
          label: item.label,
          value: item.value,
          versionDetected: item.versionDetected,
        }

        if (item.value.endsWith('-category')) {
          suggestion.icon = getIcon('technologyCategory')
        }
        else {
          suggestion.avatar = getLogo(item.value, 'technologies')
        }

        return suggestion
      })
      break
  }

  const attributesWithoutCappedSuggestions = [
    'about.businessType',
    'about.totalEmployees',
    'analytics.monthlyVisitors',
    'finances.revenue',
    'finances.stockExchange',
    'meta.listIds',
    'socials',
  ]

  suggestions.value = (suggestions?.value || [])
  // Sort suggestions by count
    .sort((suggestionA, suggestionB) => (suggestionA.count! > suggestionB.count! ? -1 : 0))
  // Remove suggestions included in current tags
    .filter(suggestion => !props.currentValues.filter(val => !!val).includes(`${suggestion.value}`.toLowerCase().trim()))
  // Cap the number of suggestions displayed
    .filter((_suggestion, suggestionIndex) => attributesWithoutCappedSuggestions.includes(props.attribute) || suggestionIndex < 25)
  // Add common properties and transform label to uppercase
    .map(
      suggestion => ({
        ...suggestion,
        addable: true,
        label: `${suggestion.label}`.toUpperCase(),
      }),
    )
}

watch(
  [
    suggestionQuery,
    () => props.attribute,
  ],
  async () => {
    await fetchSuggestions()
    generateSuggestions()
  },
  { immediate: true },
)

async function onSuggestionSelected(event: Event) {
  emit('suggestionSelected', event)

  await fetchSuggestions()

  generateSuggestions()
}

defineExpose({
  fetchSuggestions,
  generateSuggestions,
  inputMenuRef,
})
</script>

<template>
  <UInputMenu
    ref="inputMenuRef"
    v-model:query="suggestionQuery"
    :autocomplete="false"
    by="label"
    class="border-b border-gray-300 dark:border-gray-700"
    :class="[{ 'pr-10': hasBrowsePanel && showBrowse }]"
    :icon="getIcon('search')"
    :loading="loadingSuggestions"
    :options="suggestions"
    :placeholder="t('globals.enterValue')"
    size="md"
    :ui="{ leading: { padding: { md: 'ps-7' } }, icon: { leading: { padding: { md: 'px-0' } } } }"
    variant="none"
    @change="onSuggestionSelected($event)"
    @click="onInputClick($event)"
    @focus="onInputFocused($event)"
    @keydown.enter.prevent="emit('submitSuggestionQuery', $event)"
  >
    <template #option="{ option }">
      <div class="flex items-start gap-1.5">
        <UAvatar
          v-if="option.avatar"
          :alt="option.label"
          class="mt-0.5 flex-shrink-0"
          size="2xs"
          :src="option?.avatar"
        />

        <UIcon
          v-else-if="option.icon"
          class="mt-0.5 size-5 flex-shrink-0"
          :name="option?.icon"
        />

        <BaseCircle
          v-else-if="option.circle"
          class="mt-1.5 size-3 flex-shrink-0"
          :color="option.circle"
        />

        {{ option.label }}
      </div>
    </template>

    <template
      v-if="hasBrowsePanel && showBrowse"
      #trailing
    >
      <UButton
        class="flex h-full cursor-pointer select-none flex-col items-center justify-center px-0 text-xs uppercase text-gray-400 hover:text-gray-900 focus-visible:text-gray-900 focus-visible:underline focus-visible:ring-0 dark:text-gray-500 dark:hover:text-white dark:focus-visible:text-white"
        variant="link"
        @click.stop="
          toggleSlideover({
            opened: true,
            type: 'suggestions',
          })
        "
      >
        {{ t('globals.browse') }}
      </UButton>
    </template>

    <template
      v-else
      #trailing
    >
      <div />
    </template>

    <template #option-empty="{ query }">
      {{ t('globals.noResultsFor', { query }) }}
    </template>
  </UInputMenu>
</template>
